import axios, { type AxiosInstance } from 'axios';

import type {
  CheckoutSessionRequest,
  PlansResponse,
  SessionUrlResponse,
  TopUpSessionRequest,
} from '@flintmint/core-shared/billing';
import { authDenialReasons, type AuthDenialReason } from '@flintmint/core-shared/constants';

import { ENVIRONMENT } from '@/constants';
import { apiRoutes } from '@/constants/routes';

export type FirebaseAuthResponse = {
  token: string;
  isNewUser: boolean;
  signupDate?: string;
};

export const getAuthDenialReason = (error: unknown): AuthDenialReason | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) return null;
  const body = error.response.data as { error?: string } | undefined;
  return authDenialReasons.find((reason) => reason === body?.error) ?? null;
};

export class ApiService {
  private readonly api: AxiosInstance;
  private readonly baseURL = `${ENVIRONMENT.VITE_API_HOST}/api`;

  constructor(
    private readonly getAccessToken: () => Promise<string | null>,
    private readonly onAuthDenial: (authDenial: AuthDenialReason) => void
  ) {
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.api.interceptors.request.use(async (config) => {
      const token = await this.getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.handleAuthError();
        }
        return Promise.reject(error);
      }
    );
  }

  private handleAuthError(): void {
    console.error('Authentication error');
    // wtf
    // TODO log user out
  }

  public async getFirebaseToken(options?: { signal?: AbortSignal }): Promise<FirebaseAuthResponse> {
    const response = await this.api.get<FirebaseAuthResponse>(apiRoutes.firebaseAuth, { signal: options?.signal });
    return response.data;
  }

  public async checkAccess(): Promise<void> {
    await this.api.get(apiRoutes.accessCheck);
  }

  public async acceptAgreements(): Promise<void> {
    await this.api.post(apiRoutes.acceptAgreements);
  }

  public async getPlans(): Promise<PlansResponse> {
    const response = await this.reportingAuthDenial(this.api.get<PlansResponse>(apiRoutes.billingPlans));
    return response.data;
  }

  public async createCheckoutSession(request: CheckoutSessionRequest): Promise<SessionUrlResponse> {
    const response = await this.reportingAuthDenial(this.api.post<SessionUrlResponse>(apiRoutes.billingCheckoutSession, request));
    return response.data;
  }

  public async createTopUpSession(request: TopUpSessionRequest): Promise<SessionUrlResponse> {
    const response = await this.reportingAuthDenial(this.api.post<SessionUrlResponse>(apiRoutes.billingTopUpSession, request));
    return response.data;
  }

  public async createPortalSession(request: { returnUrl: string }): Promise<SessionUrlResponse> {
    const response = await this.reportingAuthDenial(this.api.post<SessionUrlResponse>(apiRoutes.billingPortalSession, request));
    return response.data;
  }

  public async cancelSubscription(): Promise<void> {
    await this.reportingAuthDenial(this.api.post(apiRoutes.billingCancel));
  }

  public async undoCancelSubscription(): Promise<void> {
    await this.reportingAuthDenial(this.api.post(apiRoutes.billingUndoCancel));
  }

  public async endTrial(): Promise<void> {
    await this.reportingAuthDenial(this.api.post(apiRoutes.billingEndTrial));
  }

  // A running session only learns that its access changed from a refused request, so billing
  // requests hand a denial to the session and still reject, letting the caller end its own state.
  // The auth routes are left out: their callers in the services provider route a denial themselves.
  private async reportingAuthDenial<T>(request: Promise<T>): Promise<T> {
    try {
      return await request;
    } catch (error) {
      const authDenial = getAuthDenialReason(error);
      if (authDenial) this.onAuthDenial(authDenial);
      throw error;
    }
  }
}
